import { Select, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { GetReportSchedulesWithExtendedReport } from "shared/api";
import { TableButton, TableSearchFilter } from "shared/ui";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { FilterFilled } from "@ant-design/icons";
import classNames from "classnames";

dayjs.extend(utc);
dayjs.extend(timezone);

interface useGetEmailsColumnsProps {
  showDeleteUserModal: (id: number) => void;
  showEditEmailModal: (id: number) => void;
  reportTypeOptions:
    | {
        label: string;
        value: string;
      }[]
    | undefined;
  filters: {
    report: {
      value: string[];
      onChange: (value: string[]) => void;
      options?:
        | {
            label: string;
            value: string;
          }[]
        | undefined;
    };
    recipient: {
      value: string;
      onChange: (e: string | React.ChangeEvent<HTMLInputElement>) => void;
      clear: () => void;
    };
    periodicity: {
      value: string;
      onChange: (value: string) => void;
    };
  };
  runTestReport: (reportId: number) => Promise<void>;
}

const storedTimeZone =
  typeof window !== "undefined"
    ? localStorage.getItem("timezone") || dayjs.tz.guess()
    : dayjs.tz.guess();

export const useGetEmailsColumns = ({
  showDeleteUserModal,
  showEditEmailModal,
  filters,
  runTestReport,
}: useGetEmailsColumnsProps): ColumnsType<GetReportSchedulesWithExtendedReport> => [
  {
    title: "Report",
    dataIndex: ["report", "name"],
    key: "report",
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": filters.report.value.length > 0,
        })}
      />
    ),
    filterDropdown: () => (
      <div style={{ padding: 8, width: 250 }}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder="Select report names"
          options={filters.report.options}
          value={filters.report.value}
          onChange={(vals) => filters.report.onChange(vals)}
          style={{ width: "100%" }}
        />
      </div>
    ),
  },
  {
    title: "Recipients",
    dataIndex: "recipients",
    key: "recipients",
    filterIcon: (
      <FilterFilled
        className={
          filters.recipient.value.length
            ? "!text-blue-600  scale-125"
            : undefined
        }
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={filters.recipient.clear}
        confirm={confirm}
        value={filters.recipient.value!}
        handleFilterChange={filters.recipient.onChange!}
        visible={visible}
        placeholder="Enter the recipient email"
        title="Recipient email"
      />
    ),
    render: (recipients: string[]) =>
      recipients?.map((email) => (
        <Tag color="blue" key={email} style={{ cursor: "pointer" }}>
          {email}
        </Tag>
      )),
  },
  {
    title: "Time",
    dataIndex: "time",
    render: (time: string) => (
      <span>
        {time
          ? dayjs
              .utc(`${dayjs().format("YYYY-MM-DD")}T${time}`)
              .tz(storedTimeZone)
              .format("HH:mm:ss")
          : "-"}
      </span>
    ),
  },
  {
    title: "Start Periodicity",
    dataIndex: "start_periodicity",
    render: (date: string) => (
      <span>{date ? dayjs(date).utc().format("YYYY-MM-DD") : "-"}</span>
    ),
  },
  {
    title: "End Periodicity",
    dataIndex: "end_periodicity",
    render: (date: string) => (
      <span>{date ? dayjs(date).utc().format("YYYY-MM-DD") : "-"}</span>
    ),
  },
  {
    title: "Periodicity",
    dataIndex: "periodicity",
    key: "periodicity",
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": filters.periodicity.value.length > 0,
        })}
      />
    ),
    filterDropdown: () => (
      <div style={{ padding: 8, width: 200 }}>
        <Select
          allowClear
          placeholder="Select periodicity"
          options={[
            { label: "Daily", value: "daily" },
            { label: "Weekly", value: "weekly" },
            { label: "Monthly", value: "monthly" },
            { label: "Yearly", value: "yearly" },
          ]}
          value={filters.periodicity.value}
          onChange={(val) => filters.periodicity.onChange(val || "")}
          style={{ width: "100%" }}
        />
      </div>
    ),
  },

  {
    title: "Last Execution",
    dataIndex: "last_execution",
    render: (date: string) => (
      <span>
        {date
          ? dayjs.utc(date).tz(storedTimeZone).format("YYYY-MM-DD HH:mm:ss")
          : "-"}
      </span>
    ),
  },
  {
    title: "Next Execution",
    dataIndex: "next_execution",
    render: (date: string) => (
      <span>
        {date
          ? dayjs.utc(date).tz(storedTimeZone).format("YYYY-MM-DD HH:mm:ss")
          : "-"}
      </span>
    ),
  },
  {
    title: "Enabled",
    dataIndex: "enabled",
    key: "enabled",
    render: (value: boolean) =>
      value ? (
        <span className="font-bold text-green-500">Enabled</span>
      ) : (
        <span className="font-bold text-red-600">Disabled</span>
      ),
  },
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <div className="flex gap-4 items-center">
        <TableButton
          type="edit"
          onClick={() => showEditEmailModal(record.id)}
        />
        <TableButton
          type="delete"
          onClick={() => showDeleteUserModal(record.id)}
        />

        {["T1", "T3"].includes(record.report.name) && (
          <TableButton
            type="eye"
            popoverString={`Test ${record.report.name} Report `}
            onClick={() => runTestReport(record.report.id)}
          />
        )}
      </div>
    ),
  },
];
